import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Loader2, ArrowRight, CheckCircle2, ExternalLink, Globe } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { isProlificTester } from "@/lib/prolific";
import {
    SCREENER_QUESTIONS,
    needsScreener,
} from "@/lib/screener";
import { useStudyConfig } from "@/lib/useStudyConfig";
import { useStudyI18n } from "@/lib/studyI18n";

/**
 * Pre-screener gate for testers.
 *
 * Five quick qualifying questions (relationship, frequency, active 30d,
 * country, age). On submit:
 *  - Qualified → navigate to /dashboard, real study begins (30-min timer
 *    starts ticking from tester_used_at, which was set on /tester login).
 *  - Not qualified → show the "thank you, this study isn't for you" screen,
 *    suspend the account so the iid can't be reused, and (for Prolific
 *    testers) hand them the Prolific screen-out completion URL.
 */
export default function Screener() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null); // { qualified, failed_on }
    const prolific = isProlificTester();
    const studyConfig = useStudyConfig();
    const screenOutCode = studyConfig.screen_out_code;
    const screenOutUrl = studyConfig.screen_out_url;
    // i18n — derives language from profile.country (set by the auth/tester
    // flow) and lets the tester override via the language picker.
    const i18n = useStudyI18n(user);
    const t = i18n.screener;

    // Already screened? Bounce them to the right place.
    useEffect(() => {
        if (!user || user === false) return;
        if (user.role === "admin") {
            navigate("/admin", { replace: true });
            return;
        }
        if (!user.tester_code) {
            // Non-testers don't need the screener — go straight to dashboard.
            navigate("/dashboard", { replace: true });
            return;
        }
        if (user.screener?.completed_at) {
            if (user.screener.qualified) {
                navigate("/dashboard", { replace: true });
            } else {
                setResult({ qualified: false, failed_on: user.screener.failed_on || [] });
            }
        }
    }, [user, navigate]);

    if (user === null) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-cream">
                <Loader2 className="animate-spin text-teal-dark" />
            </div>
        );
    }

    // Quiet guard: ProtectedRoute already keeps non-auth visitors out.
    if (user === false) {
        navigate("/", { replace: true });
        return null;
    }

    // If we've already computed a screen-out result this session, show that.
    if (result && !result.qualified) {
        return <ScreenedOutCard prolific={prolific} />;
    }

    if (!needsScreener(user) && user.screener?.qualified) {
        // Defensive — shouldn't happen because the effect above navigates,
        // but render nothing instead of double-navigating.
        return null;
    }

    const allAnswered = SCREENER_QUESTIONS.every((q) => !!answers[q.id]);

    const submit = async () => {
        if (!allAnswered) {
            toast.error("Pick an answer for every question.");
            return;
        }
        setSubmitting(true);
        try {
            const r = await api.post("/me/screener", { answers });
            await refresh();
            if (r.data.qualified) {
                toast.success(t.qualified || "Thanks — you qualified. Starting the study now.");
                navigate("/dashboard", { replace: true });
            } else {
                setResult({ qualified: false, failed_on: r.data.failed_on || [] });
                // Suspend the account so the iid is single-use even on
                // screen-outs. (Prolific testers still get paid via the
                // screen-out completion URL we hand them below.)
                try { await api.post("/auth/tester-complete"); } catch (_e) { /* noop */ }
            }
        } catch (e) {
            const msg = e?.response?.data?.detail || "Couldn't submit. Try again.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-cream">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 md:py-12">
                {/* Language picker — top-right, prominent enough to find but
                    quiet enough not to compete with the questions. */}
                <div className="flex justify-end mb-3">
                    <label className="inline-flex items-center gap-1.5 text-xs text-[#4C5551] bg-white border border-cream-dark rounded-full pl-2 pr-1 py-1 shadow-sm" data-testid="screener-lang-picker">
                        <Globe size={12} strokeWidth={2} className="text-stage_late-dark" />
                        <select
                            value={i18n.lang}
                            onChange={(e) => i18n.setLang(e.target.value)}
                            className="bg-transparent text-xs focus:outline-none cursor-pointer pr-1"
                            aria-label="Language"
                            data-testid="screener-lang-select"
                        >
                            {i18n.options.map((o) => (
                                <option key={o.code} value={o.code}>{o.label}</option>
                            ))}
                        </select>
                    </label>
                </div>
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-3xl bg-stage_late-light mb-4">
                        <Heart strokeWidth={1.6} size={22} className="text-stage_late-dark" />
                    </div>
                    <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-[#12776F]">
                        {t.intro_title}
                    </h1>
                    <p className="mt-3 text-base text-[#4C5551] leading-relaxed">
                        {t.intro_body}
                    </p>
                </div>

                <ol className="space-y-5" data-testid="screener-form">
                    {SCREENER_QUESTIONS.map((q, idx) => {
                        const tq = t.questions?.[q.id] || {};
                        return (
                            <li
                                key={q.id}
                                data-testid={`screener-q-${q.id}`}
                                className="ml-card p-5"
                            >
                                <div className="flex items-start gap-3 mb-3">
                                    <span className="shrink-0 w-7 h-7 rounded-full bg-stage_late-light text-stage_late-dark font-medium text-sm flex items-center justify-center">
                                        {idx + 1}
                                    </span>
                                    <div className="flex-1">
                                        <p className="font-heading font-medium text-base text-[#1F2624] leading-snug">
                                            {tq.text || q.text}
                                        </p>
                                        {(tq.sub || q.sub) && (
                                            <p className="text-xs text-[#4C5551] mt-1">{tq.sub || q.sub}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {q.options.map((opt) => {
                                        const selected = answers[q.id] === opt.value;
                                        // Country option labels keep their flag + English
                                        // country name (countries.js is the source of truth).
                                        // For the "outside_supported" entry we swap in the
                                        // localized "Somewhere else" copy.
                                        let label = opt.label;
                                        if (q.id === "country" && opt.value === "outside_supported") {
                                            label = t.outside_supported_label || label;
                                        } else if (tq.options && tq.options[opt.value]) {
                                            label = tq.options[opt.value];
                                        }
                                        return (
                                            <button
                                                type="button"
                                                key={opt.value}
                                                data-testid={`screener-q-${q.id}-opt-${opt.value}`}
                                                onClick={() => setAnswers((a) => ({ ...a, [q.id]: opt.value }))}
                                                className={`text-left px-4 py-3 rounded-2xl border-2 text-sm font-medium transition-colors min-h-[44px] ${selected
                                                    ? "bg-stage_late-dark text-white border-stage_late-dark"
                                                    : "bg-white text-[#1F2624] border-[#E2E8F0] hover:border-stage_late-dark/40"
                                                    }`}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </li>
                        );
                    })}
                </ol>

                <div className="mt-7 flex flex-col items-center">
                    <button
                        type="button"
                        onClick={submit}
                        disabled={!allAnswered || submitting}
                        data-testid="screener-submit"
                        className="inline-flex items-center justify-center gap-2 bg-stage_late-dark hover:bg-stage_late-dark/90 active:bg-stage_late-dark/80 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-2xl px-7 py-3.5 text-sm sm:text-base font-medium w-full sm:w-auto min-h-[48px]"
                    >
                        {submitting ? (
                            <><Loader2 size={16} className="animate-spin" /> {t.submit_busy || "Submitting…"}</>
                        ) : (
                            <>{t.submit || "Continue"} <ArrowRight size={16} strokeWidth={2} /></>
                        )}
                    </button>
                    <p className="mt-3 text-xs text-[#4C5551] text-center max-w-md leading-snug" data-testid="screener-consent">
                        By continuing, you confirm you&apos;ve read the{" "}
                        <a
                            href="/terms"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="screener-terms-link"
                            className="text-teal-dark underline"
                        >
                            Terms of Service
                        </a>{" "}
                        and understand Memory Lane is not a HIPAA-covered service.
                    </p>
                    <p className="mt-2 text-xs text-[#6E7A74] text-center">
                        We only use these answers to decide if Memory Lane is built for you right now —
                        no other purpose, no marketing list.
                    </p>
                </div>
            </div>
        </div>
    );
}

function ScreenedOutCard({ prolific }) {
    const studyConfig = useStudyConfig();
    const screenOutCode = studyConfig.screen_out_code;
    const screenOutUrl = studyConfig.screen_out_url;
    const copyAndGo = () => {
        try {
            navigator.clipboard.writeText(screenOutCode);
            toast.success("Code copied.");
        } catch { /* noop */ }
        window.location.href = screenOutUrl;
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-cream p-4">
            <div className="max-w-lg w-full ml-card p-7 sm:p-9 text-center" data-testid="screened-out-card">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-3xl bg-stage_late-light mb-4">
                    <CheckCircle2 strokeWidth={1.6} size={22} className="text-stage_late-dark" />
                </div>
                <h1 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight text-[#12776F]">
                    Thank you for your honesty.
                </h1>
                <p className="mt-4 text-base text-[#4C5551] leading-relaxed">
                    Based on your answers, Memory Lane isn&apos;t built for your situation right now —
                    we&apos;re currently focused on family caregivers actively caring for someone with
                    dementia in the US. That&apos;s a narrow group, and we&apos;d rather not waste your
                    time pretending otherwise.
                </p>
                {prolific ? (
                    <>
                        <p className="mt-4 text-sm text-[#1F2624]">
                            You&apos;ll still be paid for showing up — tap the button below to return to
                            Prolific and submit your screen-out code.
                        </p>
                        <button
                            type="button"
                            onClick={copyAndGo}
                            data-testid="screened-out-return-prolific"
                            className="mt-6 inline-flex items-center justify-center gap-2 bg-stage_late-dark hover:bg-stage_late-dark/90 active:bg-stage_late-dark/80 text-white rounded-2xl px-6 py-3.5 text-sm font-medium w-full min-h-[48px]"
                        >
                            Return to Prolific
                            <ExternalLink size={14} strokeWidth={1.8} />
                        </button>
                        <p className="mt-3 text-xs text-[#4C5551]">
                            If the button doesn&apos;t load, copy code <code className="font-mono text-stage_late-dark font-semibold">{screenOutCode}</code> and paste it on Prolific.
                        </p>
                    </>
                ) : (
                    <>
                        <p className="mt-4 text-sm text-[#1F2624]">
                            If your situation changes, you&apos;re welcome back — keep an eye on
                            memorylanecare.net.
                        </p>
                        <a
                            href="https://www.memorylanecare.net"
                            target="_blank"
                            rel="noreferrer"
                            data-testid="screened-out-home-link"
                            className="mt-6 inline-flex items-center justify-center gap-2 bg-white hover:bg-cream-dark border border-stage_late-dark text-stage_late-dark rounded-2xl px-6 py-3.5 text-sm font-medium w-full min-h-[48px]"
                        >
                            Back to memorylanecare.net
                            <ExternalLink size={14} strokeWidth={1.8} />
                        </a>
                    </>
                )}
            </div>
        </div>
    );
}
